import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ACCENT } from "../artistStudio/shared";
import { soundApi } from "../soundDetail/soundApi";
import { hasOffline, offlineUrl } from "../soundDetail/offlineStore";
import { fanHomeApi, apiStatus, sendPlaybackOnExit, formatClock, isSignedIn } from "./fanHomeApi";

// One player for the whole app: only one track plays at a time, and playback carries on while the fan moves between
// pages. Plays and the resume position are saved for signed-in fans through the Fan Home API.
const PlayerContext = createContext(null);
const PROGRESS_EVERY_MS = 15000;

// Where a play started, for the artist's audience numbers (FSD 6.3.4): the page the fan is on when the track is played.
export const playSource = (pathname = "") => {
  const path = String(pathname).toLowerCase();
  if (path.startsWith("/sound")) return "sound_page";
  if (path.startsWith("/artist")) return "artist_page";
  if (path.startsWith("/library") || path.startsWith("/fan/library")) return "library";
  if (path.startsWith("/search") || path.startsWith("/music/search")) return "search";
  if (path === "/" || path.startsWith("/music") || path.startsWith("/home")) return "home";
  return "other";
};
let currentLocalUrl = null; // address of an offline copy being played, released when another track starts

export const usePlayer = () => {
  const value = useContext(PlayerContext);
  if (!value) throw new Error("usePlayer must be used inside PlayerProvider");
  return value;
};

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  if (audioRef.current === null && typeof Audio !== "undefined") audioRef.current = new Audio();
  const trackRef = useRef(null);
  const sourceRef = useRef("other");
  const pendingStart = useRef(0);

  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(() => new Set());
  // The list the fan started from (a section, a search, a playlist). Next / Previous and auto-advance move along it.
  const [queue, setQueue] = useState([]);
  const queueRef = useRef([]);
  const advanceRef = useRef(() => {});
  // Shuffle and repeat (off | all | one), used by Next and by auto-advance; "playingFrom" labels the full-screen player.
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("off");
  const shuffleRef = useRef(false);
  const repeatRef = useRef("off");
  const [playingFrom, setPlayingFrom] = useState("other");
  // Volume (0 to 1) and mute for the player; the level is remembered in this browser (a failed read or write just keeps the default).
  const [volume, setVolumeState] = useState(() => {
    try { const v = Number(window.localStorage.getItem("pipaanVolume")); return Number.isFinite(v) && v >= 0 && v <= 1 && window.localStorage.getItem("pipaanVolume") !== null ? v : 1; } catch (e) { return 1; }
  });
  const [muted, setMuted] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) { audio.volume = volume; audio.muted = muted; }
  }, [volume, muted, track]);
  const setVolume = useCallback((v) => {
    const level = Math.min(1, Math.max(0, Number(v) || 0));
    setVolumeState(level);
    setMuted(level === 0);
    try { window.localStorage.setItem("pipaanVolume", String(level)); } catch (e) { /* not remembered */ }
  }, []);
  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  const markUnavailable = useCallback((id) => {
    setUnavailable((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const report = useCallback((event) => {
    const current = trackRef.current;
    const audio = audioRef.current;
    if (!current || !audio || !isSignedIn()) return;
    fanHomeApi
      .playback({ track_id: current.id, event, position_seconds: Math.floor(audio.currentTime || 0), source: sourceRef.current })
      .catch((e) => {
        // The server says the track can no longer be played.
        if (apiStatus(e) === 410) {
          markUnavailable(current.id);
          audio.pause();
          setError("This track is no longer available.");
        }
        // Not allowed to listen: a membership is needed, or the track is not cleared for this country.
        if (apiStatus(e) === 403) {
          audio.pause();
          setError(e?.response?.data?.message || "You can't listen to this track right now.");
        }
      });
  }, [markUnavailable]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const on = {
      playing: () => { setPlaying(true); setLoading(false); setError(""); },
      pause: () => { setPlaying(false); if (!audio.ended) report("pause"); },
      waiting: () => setLoading(true),
      loadedmetadata: () => {
        setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
        if (pendingStart.current > 0 && pendingStart.current < (audio.duration || Infinity) - 3) audio.currentTime = pendingStart.current;
        pendingStart.current = 0;
      },
      timeupdate: () => setPosition(audio.currentTime || 0),
      ended: () => { setPlaying(false); report("end"); advanceRef.current(1, true); },
      error: () => {
        setPlaying(false);
        setLoading(false);
        if (trackRef.current) markUnavailable(trackRef.current.id);
        setError("This track can't be played right now.");
      },
    };
    Object.entries(on).forEach(([name, fn]) => audio.addEventListener(name, fn));
    return () => Object.entries(on).forEach(([name, fn]) => audio.removeEventListener(name, fn));
  }, [report, markUnavailable]);

  // Save the position every few seconds while playing, and when the page is closed.
  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => report("progress"), PROGRESS_EVERY_MS);
    return () => clearInterval(timer);
  }, [playing, report]);

  useEffect(() => {
    const onExit = () => {
      const audio = audioRef.current;
      const current = trackRef.current;
      if (audio && current && !audio.paused) sendPlaybackOnExit({ track_id: current.id, event: "pause", position_seconds: Math.floor(audio.currentTime || 0), source: sourceRef.current });
    };
    window.addEventListener("pagehide", onExit);
    return () => window.removeEventListener("pagehide", onExit);
  }, []);

  // source: where the play started when it is not just the page (a paid promotion is "promoted": royalties leave those plays out).
  const play = useCallback((next, { startAt = 0, queue: list, source } = {}) => {
    const audio = audioRef.current;
    if (!audio || !next || !next.audio_url) return;
    // Remember the list this track came from; a track played on its own becomes a list of one.
    if (Array.isArray(list) && list.some((t) => t.id === next.id)) queueRef.current = list;
    else if (!queueRef.current.some((t) => t.id === next.id)) queueRef.current = [next];
    setQueue(queueRef.current);
    // The same track: play / pause, nothing restarts.
    if (trackRef.current && trackRef.current.id === next.id && !error) {
      if (audio.paused) {
        // Resuming must restart the listening clock on the server (a pause stops it); a track that played to the end starts a new listen.
        const finished = audio.ended;
        audio.play().then(() => report(finished ? "start" : "progress")).catch(() => {});
      } else audio.pause();
      return;
    }
    if (trackRef.current && !audio.paused) report("pause");
    trackRef.current = next;
    sourceRef.current = source || playSource(window.location.pathname);
    setPlayingFrom(sourceRef.current);
    pendingStart.current = Number(startAt) || 0;
    setTrack(next);
    setError("");
    setLoading(true);
    setPosition(Number(startAt) || 0);
    setDuration(next.duration || 0);
    if (currentLocalUrl) { URL.revokeObjectURL(currentLocalUrl); currentLocalUrl = null; }
    const begin = (src) => {
      audio.src = src;
      audio.play().then(() => report("start")).catch(() => {
        setLoading(false);
        setPlaying(false);
        setError("This track can't be played right now.");
      });
    };
    if (hasOffline(next.id)) {
      // A sound saved for offline listening plays from Pipaan's own storage; if that copy cannot be read, it streams as usual.
      offlineUrl(next.id)
        .then((local) => {
          if (!trackRef.current || trackRef.current.id !== next.id) { if (local) URL.revokeObjectURL(local); return; }
          currentLocalUrl = local;
          begin(local || next.audio_url);
        })
        .catch(() => begin(next.audio_url));
    } else {
      begin(next.audio_url);
    }
  }, [report, error]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !trackRef.current) return;
    if (audio.paused) {
      const finished = audio.ended;
      audio.play().then(() => report(finished ? "start" : "progress")).catch(() => {});
    } else audio.pause();
  }, [report]);

  const seek = useCallback((seconds) => {
    const audio = audioRef.current;
    if (audio && Number.isFinite(seconds)) audio.currentTime = Math.max(0, seconds);
  }, []);

  const close = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.removeAttribute("src"); audio.load(); }
    trackRef.current = null;
    queueRef.current = [];
    setQueue([]);
    setTrack(null);
    setPlaying(false);
    setError("");
    setPosition(0);
  }, []);

  // Move to the next (1) or previous (-1) playable track in the queue. Shuffle picks any other playable track for Next; Repeat one
  // starts a track again when it ends; Repeat all goes back to the start of the list after its last track. With neither, auto-advance
  // just stops at the end of the queue.
  const advance = useCallback((step, fromEnd = false) => {
    const list = queueRef.current;
    const current = trackRef.current;
    if (!current || !list.length) return;
    const audio = audioRef.current;
    const playable = (t) => t.audio_url && !unavailable.has(t.id);
    if (fromEnd && repeatRef.current === "one" && audio) {
      audio.currentTime = 0;
      audio.play().then(() => report("start")).catch(() => {});
      return;
    }
    if (step < 0 && audio && audio.currentTime > 3) { audio.currentTime = 0; return; } // like most players: restart first
    const at = list.findIndex((t) => t.id === current.id);
    if (step > 0 && shuffleRef.current) {
      const others = list.filter((t) => t.id !== current.id && playable(t));
      if (others.length) { play(others[Math.floor(Math.random() * others.length)]); return; }
    } else {
      for (let i = at + step; i >= 0 && i < list.length; i += step) {
        if (playable(list[i])) { play(list[i]); return; }
      }
    }
    if (step > 0 && repeatRef.current === "all" && audio) {
      const first = list.find(playable);
      if (first && first.id !== current.id) { play(first); return; }
      if (first) { audio.currentTime = 0; audio.play().then(() => report("start")).catch(() => {}); return; }
    }
    if (!fromEnd && audio && step < 0) audio.currentTime = 0;
  }, [play, unavailable, report]);
  advanceRef.current = advance;

  const clearUpNext = useCallback(() => {
    const current = trackRef.current;
    queueRef.current = current ? [current] : [];
    setQueue(queueRef.current);
  }, []);

  const toggleShuffle = useCallback(() => { shuffleRef.current = !shuffleRef.current; setShuffle(shuffleRef.current); }, []);
  const cycleRepeat = useCallback(() => {
    const mode = repeatRef.current === "off" ? "all" : repeatRef.current === "all" ? "one" : "off";
    repeatRef.current = mode;
    setRepeat(mode);
  }, []);

  const index = track ? queue.findIndex((t) => t.id === track.id) : -1;
  const canStep = (step) => {
    for (let i = index + step; i >= 0 && i < queue.length; i += step) if (queue[i].audio_url && !unavailable.has(queue[i].id)) return true;
    return false;
  };

  const isPlayable = (t) => t.audio_url && !unavailable.has(t.id);
  const hasNextTrack = canStep(1) || (shuffle && queue.some((t) => track && t.id !== track.id && isPlayable(t))) || (repeat === "all" && queue.some(isPlayable));

  const value = useMemo(() => ({
    track, playing, loading, position, duration, error, unavailable, play, toggle, seek, close,
    queue, next: () => advance(1), previous: () => advance(-1), hasNext: hasNextTrack, hasPrevious: index > 0, clearUpNext,
    shuffle, repeat, toggleShuffle, cycleRepeat, playingFrom, volume, muted, setVolume, toggleMute,
    isCurrent: (id) => Boolean(track && track.id === id),
    isUnavailable: (id) => unavailable.has(id),
  }), [track, playing, loading, position, duration, error, unavailable, play, toggle, seek, close, queue, advance, clearUpNext, shuffle, repeat, playingFrom, toggleShuffle, cycleRepeat, hasNextTrack, volume, muted, setVolume, toggleMute]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <MiniPlayer />
    </PlayerContext.Provider>
  );
}

const iconProps = { viewBox: "0 0 24 24", className: "h-5 w-5", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" };
const PrevIcon = () => <svg {...iconProps} fill="currentColor" stroke="none"><path d="M6 5h2v14H6zM20 5v14L9 12z" /></svg>;
const NextIcon = () => <svg {...iconProps} fill="currentColor" stroke="none"><path d="M16 5h2v14h-2zM4 5v14l11-7z" /></svg>;
const ShuffleIcon = () => <svg {...iconProps}><path d="M16 3h5v5M4 20 21 3M21 16v5h-5M15 15l6 6M4 4l5 5" /></svg>;
const RepeatIcon = () => <svg {...iconProps}><path d="m17 2 4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" /><path d="m7 22-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" /></svg>;
const ChevronDownIcon = () => <svg {...iconProps}><path d="m6 9 6 6 6-6" /></svg>;
const ExpandIcon = () => <svg {...iconProps}><path d="m18 15-6-6-6 6" /></svg>;
const QueueIcon = () => <svg {...iconProps}><path d="M3 6h13M3 12h13M3 18h7M18 14v6l4-3z" /></svg>;
const ShareIcon = () => <svg {...iconProps}><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" /></svg>;
const HeartIcon = ({ filled }) => <svg {...iconProps} fill={filled ? "currentColor" : "none"}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8Z" /></svg>;

const iconButton = "h-9 w-9 rounded-full flex items-center justify-center shrink-0 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent";

// The saved (heart) state of the playing sound, with an optimistic toggle. Guests are not offered it.
function useSavedState(trackId) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSaved(false);
    if (!trackId || !isSignedIn()) return undefined;
    let live = true;
    soundApi.detail(trackId).then((d) => live && setSaved(Boolean(d?.saved))).catch(() => {});
    return () => { live = false; };
  }, [trackId]);
  const toggle = async () => {
    if (busy || !trackId) return;
    const was = saved;
    setSaved(!was);
    setBusy(true);
    try {
      if (was) await soundApi.unsave(trackId); else await soundApi.save(trackId);
      toast.success(was ? "Removed from saved sounds." : "Saved to My Library.");
    } catch (e) {
      setSaved(was);
      toast.error("We couldn't update your saved sounds. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return { saved, toggle, busy };
}

function QueuePanel({ onClose, above = false }) {
  const { track, queue, play, clearUpNext } = usePlayer();
  const index = queue.findIndex((t) => t.id === track.id);
  const upNext = index >= 0 ? queue.slice(index + 1) : [];
  const row = (t, current) => (
    <li key={t.id}>
      <button type="button" onClick={() => !current && play(t)} disabled={current || !t.audio_url} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-gray-50 disabled:hover:bg-transparent">
        {t.cover_url ? <img src={t.cover_url} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" /> : <div className="h-10 w-10 rounded-lg bg-gray-200 shrink-0" aria-hidden="true" />}
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-sm font-semibold ${current ? "" : "text-gray-900"}`} style={current ? { color: ACCENT } : undefined}>{t.title}</span>
          <span className="block truncate text-xs text-gray-500">{t.artist_name}{!t.audio_url ? " · Unavailable" : ""}</span>
        </span>
        <span className="text-xs text-gray-400">{formatClock(t.duration)}</span>
      </button>
    </li>
  );
  return (
    <div className={`fixed inset-x-0 px-3 ${above ? "bottom-4 z-[70]" : "bottom-[76px] z-50"}`} role="dialog" aria-label="Queue">
      <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-4 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Queue</h2>
          <button type="button" onClick={onClose} aria-label="Close queue" className="text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
        </div>
        <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Now playing</p>
        <ul>{row(track, true)}</ul>
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Up next</p>
          {upNext.length > 0 && <button type="button" onClick={clearUpNext} className="text-xs font-semibold hover:underline" style={{ color: ACCENT }}>Clear</button>}
        </div>
        {upNext.length ? <ul className="max-h-64 overflow-y-auto" data-testid="up-next">{upNext.map((t) => row(t, false))}</ul> : <p className="py-3 text-sm text-gray-500">Nothing queued. Play from a list and the rest of it follows.</p>}
      </div>
    </div>
  );
}

async function shareTrack(track) {
  const link = `${window.location.origin}/sound/${track.id}`;
  try {
    if (navigator.share) { await navigator.share({ title: track.title, text: `${track.title}${track.artist_name ? ` by ${track.artist_name}` : ""} on Nyawoo`, url: link }); return; }
    await navigator.clipboard.writeText(link);
    toast.success("Link copied.");
  } catch (e) {
    if (e && e.name === "AbortError") return; // the share sheet was dismissed
    toast.info(link);
  }
}

const FROM_LABEL = { home: "Home", search: "Search", library: "My Library", artist_page: "the artist page", sound_page: "the sound page", promoted: "a promoted track", other: "your queue" };

// The full-screen player (mobile design, screen 16): big cover, title and artist, save, seek bar, shuffle / previous / play / next /
// repeat, share and queue. Opened from the player bar, closed with the chevron or Escape.
function FullPlayer({ onClose }) {
  const { track, playing, loading, position, duration, error, toggle, seek, next, previous, hasNext, hasPrevious, shuffle, repeat, toggleShuffle, cycleRepeat, playingFrom } = usePlayer();
  const { saved, toggle: toggleSaved, busy: savingBusy } = useSavedState(track?.id);
  const [showQueue, setShowQueue] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") (showQueue ? setShowQueue(false) : onClose()); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, showQueue]);
  if (!track) return null;
  const total = duration || track.duration || 0;
  const big = "h-12 w-12 rounded-full flex items-center justify-center shrink-0 text-white hover:bg-white/10 disabled:opacity-30";
  return (
    <div className="fixed inset-0 z-[60] flex flex-col overflow-y-auto text-white" style={{ backgroundColor: "#12301f" }} role="dialog" aria-modal="true" aria-label="Now playing" data-testid="full-player">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 pb-8 pt-5">
        <div className="flex items-center justify-between">
          <button type="button" onClick={onClose} aria-label="Close full-screen player" className={big}><ChevronDownIcon /></button>
          <p className="text-sm text-white/80">Playing from {FROM_LABEL[playingFrom] || FROM_LABEL.other}</p>
          <span className="h-12 w-12" aria-hidden="true" />
        </div>
        <div className="mt-6 flex justify-center">
          {track.cover_url ? <img src={track.cover_url} alt="" className="aspect-square w-full max-w-xs rounded-3xl object-cover shadow-xl" /> : <div className="aspect-square w-full max-w-xs rounded-3xl bg-white/15" aria-hidden="true" />}
        </div>
        <div className="mt-8 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-2xl font-bold"><Link to={`/sound/${track.id}`} onClick={onClose} className="hover:underline">{track.title}</Link></h2>
            <p className="truncate text-base text-white/80">{track.artist_slug ? <Link to={`/artists/${encodeURIComponent(track.artist_slug)}`} onClick={onClose} className="hover:underline">{track.artist_name}</Link> : track.artist_name}</p>
            {error && <p className="mt-1 text-sm text-red-300" role="alert">{error}</p>}
          </div>
          {isSignedIn() && (
            <button type="button" onClick={toggleSaved} disabled={savingBusy} aria-pressed={saved} aria-label={saved ? "Remove from saved sounds" : "Save this sound"} className={big}><HeartIcon filled={saved} /></button>
          )}
        </div>
        <div className="mt-6">
          <input
            type="range" min="0" max={Math.max(1, Math.floor(total))} value={Math.min(Math.floor(position), Math.max(1, Math.floor(total)))}
            onChange={(e) => seek(Number(e.target.value))} aria-label="Seek" className="h-1 w-full" style={{ accentColor: "#ffffff" }}
          />
          <div className="mt-1 flex justify-between text-xs text-white/70"><span>{formatClock(position)}</span><span>{formatClock(total)}</span></div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <button type="button" onClick={toggleShuffle} aria-pressed={shuffle} aria-label="Shuffle" data-testid="full-shuffle" className={big} style={shuffle ? { backgroundColor: "rgba(255,255,255,0.22)" } : undefined}><ShuffleIcon /></button>
          <button type="button" onClick={previous} disabled={!hasPrevious && position < 3} aria-label="Previous track" className={big}><PrevIcon /></button>
          <button type="button" onClick={toggle} disabled={Boolean(error)} aria-label={playing ? "Pause" : "Play"} className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white disabled:opacity-50" style={{ color: "#12301f" }}>
            {loading ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-label="Loading" /> : <PlayPauseIcon playing={playing} />}
          </button>
          <button type="button" onClick={next} disabled={!hasNext} aria-label="Next track" className={big}><NextIcon /></button>
          <button type="button" onClick={cycleRepeat} aria-pressed={repeat !== "off"} aria-label={repeat === "one" ? "Repeat one" : repeat === "all" ? "Repeat all" : "Repeat off"} data-testid="full-repeat" className={`${big} relative`} style={repeat !== "off" ? { backgroundColor: "rgba(255,255,255,0.22)" } : undefined}>
            <RepeatIcon />{repeat === "one" && <span className="absolute right-2 top-2 text-[10px] font-bold leading-none">1</span>}
          </button>
        </div>
        <div className="mt-6 flex items-center justify-between gap-2">
          <VolumeControl light />
          <div className="flex items-center gap-2">
          <button type="button" onClick={() => shareTrack(track)} aria-label="Share this sound" className={big}><ShareIcon /></button>
          <button type="button" onClick={() => setShowQueue((v) => !v)} aria-pressed={showQueue} aria-label="Queue" className={big}><QueueIcon /></button>
          </div>
        </div>
      </div>
      {showQueue && <QueuePanel above onClose={() => setShowQueue(false)} />}
    </div>
  );
}

// Mute button and volume slider for the shared player.
function VolumeControl({ light = false, className = "" }) {
  const { volume, muted, setVolume, toggleMute } = usePlayer();
  const level = muted ? 0 : volume;
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        type="button" onClick={toggleMute} aria-pressed={muted || volume === 0} aria-label={muted || volume === 0 ? "Unmute" : "Mute"} data-testid="player-mute"
        className={light ? "h-9 w-9 rounded-full flex items-center justify-center text-white/90 hover:bg-white/10" : iconButton}
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
          <path d="M3 10v4h4l5 4V6L7 10H3Z" />
          {level === 0 ? <path d="M16 9l5 6m0-6l-5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" /> : <path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />}
        </svg>
      </button>
      <input
        type="range" min="0" max="100" step="1" value={Math.round(level * 100)} onChange={(e) => setVolume(Number(e.target.value) / 100)}
        aria-label="Volume" data-testid="player-volume" className="h-1 w-20" style={{ accentColor: light ? "#ffffff" : ACCENT }}
      />
    </div>
  );
}

function MiniPlayer() {
  const { track, playing, loading, position, duration, error, toggle, seek, close, next, previous, hasNext, hasPrevious } = usePlayer();
  const { saved, toggle: toggleSaved, busy: savingBusy } = useSavedState(track?.id);
  const [showQueue, setShowQueue] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (!track) { setShowQueue(false); setExpanded(false); } }, [track]);
  if (!track) return null;
  const total = duration || track.duration || 0;
  const share = () => shareTrack(track);
  return (
    <>
      {expanded && <FullPlayer onClose={() => setExpanded(false)} />}
      {showQueue && <QueuePanel onClose={() => setShowQueue(false)} />}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-200 shadow-lg" role="region" aria-label="Music player">
        <div className="max-w-5xl mx-auto px-3 md:px-6 py-2 flex items-center gap-2 md:gap-3">
          {track.cover_url ? (
            <img src={track.cover_url} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-gray-200 shrink-0" aria-hidden="true" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate"><Link to={`/sound/${track.id}`} className="hover:underline">{track.title}</Link></p>
            <p className="text-xs text-gray-500 truncate">
              {error ? <span className="text-red-600" role="alert">{error}</span> : track.artist_slug ? <Link to={`/artists/${encodeURIComponent(track.artist_slug)}`} className="hover:underline">{track.artist_name}</Link> : track.artist_name}
            </p>
            <div className="hidden sm:flex items-center gap-2 mt-1">
              <span className="text-[11px] text-gray-500 w-9 text-right">{formatClock(position)}</span>
              <input
                type="range" min="0" max={Math.max(1, Math.floor(total))} value={Math.min(Math.floor(position), Math.max(1, Math.floor(total)))}
                onChange={(e) => seek(Number(e.target.value))} aria-label="Seek" className="flex-1 h-1" style={{ accentColor: ACCENT }}
              />
              <span className="text-[11px] text-gray-500 w-9">{formatClock(total)}</span>
            </div>
          </div>
          {isSignedIn() && (
            <button type="button" onClick={toggleSaved} disabled={savingBusy} aria-pressed={saved} aria-label={saved ? "Remove from saved sounds" : "Save this sound"} data-testid="player-save" className={iconButton} style={saved ? { color: ACCENT } : undefined}><HeartIcon filled={saved} /></button>
          )}
          <VolumeControl className="hidden md:flex" />
          <button type="button" onClick={share} aria-label="Share this sound" className={`${iconButton} hidden sm:flex`}><ShareIcon /></button>
          <button type="button" onClick={previous} disabled={!hasPrevious && position < 3} aria-label="Previous track" data-testid="player-prev" className={iconButton}><PrevIcon /></button>
          <button
            type="button" onClick={toggle} disabled={Boolean(error)} aria-label={playing ? "Pause" : "Play"}
            className="h-11 w-11 rounded-full text-white flex items-center justify-center shrink-0 disabled:opacity-50" style={{ backgroundColor: ACCENT }}
          >
            {loading ? <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" aria-label="Loading" /> : <PlayPauseIcon playing={playing} />}
          </button>
          <button type="button" onClick={next} disabled={!hasNext} aria-label="Next track" data-testid="player-next" className={iconButton}><NextIcon /></button>
          <button type="button" onClick={() => setShowQueue((v) => !v)} aria-pressed={showQueue} aria-label="Queue" data-testid="player-queue" className={iconButton} style={showQueue ? { color: ACCENT } : undefined}><QueueIcon /></button>
          <button type="button" onClick={() => setExpanded(true)} aria-label="Open full-screen player" data-testid="player-expand" className={iconButton}><ExpandIcon /></button>
          <button type="button" onClick={close} aria-label="Close player" className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1">×</button>
        </div>
      </div>
    </>
  );
}

export function PlayPauseIcon({ playing }) {
  return playing ? (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden="true"><rect x="4" y="3" width="4" height="14" rx="1" /><rect x="12" y="3" width="4" height="14" rx="1" /></svg>
  ) : (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M6 3.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L7.5 2.64A1 1 0 0 0 6 3.5Z" /></svg>
  );
}
